import ProductHero from "@/components/ProductHero";
import ProductDetail from "@/components/ProductDetail";
import ProductCTA from "@/components/ProductCTA";
import SEO from "@/components/SEO";
import SystemCombinations from "@/components/SystemCombinations";
import RelatedSystems from "@/components/RelatedSystems";
import GeoDefinition, { buildDefinedTerm, buildFaqJsonLd } from "@/components/GeoDefinition";
import ProductFaq from "@/components/ProductFaq";
import {
  IntroBlock,
  ProblemBlock,
  AudienceBlock,
  BenefitsBlock,
  ExperienceBlock,
} from "@/components/SectionBlocks";
import { SYSTEMS } from "@/lib/constants";

const s = SYSTEMS.find((x) => x.slug === "pine-places");

const PLACES_DEFINITION =
  "PINE Places ist das digitale Markenraum-System von PINE Systems. Es macht reale Orte digital begehbar, erklärbar und emotional verständlich. Aus Hotels, Eventlocations, Showrooms, Immobilien, Freizeitstandorten oder Markenräumen werden interaktive Customer Journeys, die schon vor dem Besuch Wirkung entfalten.";

const PLACES_PRODUCT_FAQ = [
  {
    q: "Was ist der Unterschied zwischen PINE Places und normalen Bildern oder Videos?",
    a: "Bilder zeigen Ausschnitte. Videos laufen linear. PINE Places macht reale Orte digital erfahrbar und führt Kunden aktiv durch Räume, Angebote und Entscheidungswege.",
  },
  {
    q: "Für welche Orte ist PINE Places geeignet?",
    a: "Hotels, Resorts, Immobilien, Eventlocations, Freizeitstandorte, Showrooms, Erlebniswelten, Markenräume und touristische Betriebe – überall dort, wo ein Standort online sichtbar, begehbar und erklärbar sein soll.",
  },
  {
    q: "Wie wird ein realer Ort zum digitalen Markenraum?",
    a: "PINE Places erstellt eine digitale Standortkopie und kombiniert sie mit einer interaktiven Customer Journey: Orientierung geben, Räume erklären, Leistungen inszenieren und Kunden gezielt durch eine Entscheidungsreise führen.",
  },
  {
    q: "Ist PINE Places nur für Hotels geeignet?",
    a: "Nein. PINE Places funktioniert überall dort, wo ein Ort vor dem Besuch verstanden werden muss – z. B. Eventlocations, Immobilien, Showrooms, Freizeitwelten oder Markenräume. Hotels sind nur ein typischer Anwendungsfall.",
  },
];

const PLACES_QA_FOR_LD = [
  {
    q: "Wie unterscheidet sich PINE Places von den anderen Systemprofilen?",
    a: "PVRA, AR_ENA, PINE VR und Hotel VR sind physische Erlebnisstandorte. PINE Places ist das digitale System: kein Erlebnisraum vor Ort, sondern ein digitaler Markenraum als Teil der Customer Journey.",
  },
  ...PLACES_PRODUCT_FAQ,
];

export default function PinePlaces() {
  return (
    <>
      <SEO
        title="PINE Places – Digitale Markenräume und Customer Journeys"
        description="PINE Places macht reale Orte digital erlebbar und verwandelt Hotels, Eventlocations, Showrooms, Immobilien und Markenräume in interaktive Customer Journeys."
        ogType="product"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "PINE Places — Digital Brand Spaces",
            brand: { "@type": "Brand", name: "PINE Systems" },
            description: PLACES_DEFINITION,
            category: "Digital Experience System",
          },
          buildDefinedTerm({
            name: "PINE Places",
            alternateName: "Digital Brand Spaces",
            description: PLACES_DEFINITION,
            url: "https://www.pinexs.de/pine-places",
          }),
          buildFaqJsonLd(PLACES_QA_FOR_LD),
        ]}
      />
      <ProductHero
        code="05 / SYSTEM"
        name="PINE Places"
        headline="Dein realer Ort als digitaler Markenraum."
        sub="PINE Places macht aus realen Welten digitale Kopien, die nicht nur zeigen, wie ein Ort aussieht, sondern Teil der Customer Journey werden."
        image={s.image}
      />

      {/* Formula */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
          <div className="overline mb-6">Core Formula</div>
          <h2 className="font-display text-2xl md:text-4xl uppercase leading-snug">
            Dein Place <span className="text-[#18E096]">+</span> PINE Places{" "}
            <span className="text-[#18E096]">=</span> Dein Markenraum.
          </h2>
        </div>
      </section>

      <IntroBlock
        overline="Positionierung"
        title="Ihr Ort beginnt nicht erst vor Ort."
        text={`Viele Kunden, Gäste und Entscheider erleben einen Standort zuerst digital. Sie recherchieren, vergleichen, zeigen den Ort intern weiter, erklären ihn anderen oder entscheiden sich auf Basis eines ersten digitalen Eindrucks.

PINE Places macht reale Orte digital begehbar, erklärbar und emotional verständlich. Aus einem Standort wird ein Markenraum, der bereits vor dem Besuch Wirkung erzeugt.`}
      />

      <ProblemBlock
        overline="Problem"
        title="Das Problem, das PINE Places löst."
        text="Viele hochwertige Orte wirken online kleiner, unklarer oder austauschbarer, als sie in Wirklichkeit sind. Bilder zeigen Ausschnitte. Texte erklären zu wenig. Grundrisse wirken trocken. Videos sind linear. Und klassische Websites schaffen oft nicht das Gefühl, den Ort wirklich zu verstehen. PINE Places übersetzt reale Orte in digitale Erfahrungsräume."
        cards={[
          {
            title: "Orte werden online oft nicht verstanden",
            text: "Kunden sehen Bilder, aber verstehen nicht, wie sich der Ort anfühlt, wie er aufgebaut ist oder wofür er genutzt werden kann.",
          },
          {
            title: "Entscheidungen brauchen Vorstellungskraft",
            text: "Hotels, Eventlocations, Showrooms und Erlebnisräume müssen online erklärbar werden, bevor jemand anfragt oder bucht.",
          },
          {
            title: "Markenräume brauchen Führung",
            text: "Ein digitaler Ort soll nicht nur zeigen, sondern durch eine Customer Journey führen.",
          },
          {
            title: "Präsenz braucht mehr als Bilder",
            text: "Ein realer Standort kann digital stärker wirken, wenn er begehbar, strukturiert und emotional inszeniert wird.",
          },
        ]}
      />

      <BenefitsBlock
        overline="Was PINE Places leistet"
        title="Was PINE Places aus Ihrem Ort macht."
        items={[
          "Digitale Standortkopie",
          "Interaktive Customer Journey",
          "Digitale Markeninszenierung",
          "Bessere Orientierung vor dem Besuch",
          "Emotionale Entscheidungshilfe",
          "Erklärung komplexer Orte",
          "Digitale Präsenz mit echtem Standortbezug",
          "Nutzbar für Website, Sales, Präsentation, Hospitality, Events und Beratung",
        ]}
      />

      <AudienceBlock
        overline="Zielgruppen"
        title="Für welche Orte PINE Places besonders passt."
        cards={[
          {
            title: "Hotels und Resorts",
            text: "Für Häuser, die Zimmer, Suiten, Spa, Restaurants, Tagungsräume oder Erlebnisangebote digital erlebbar machen möchten.",
          },
          {
            title: "Eventlocations",
            text: "Für Locations, die Kunden schon vor der Anfrage zeigen wollen, wie Räume wirken, Wege funktionieren und Events gedacht werden können.",
          },
          {
            title: "Immobilien & Showrooms",
            text: "Für Orte, die nicht nur präsentiert, sondern verstanden und erklärt werden müssen.",
          },
          {
            title: "Freizeit- und Erlebnisstandorte",
            text: "Für Anbieter, die ihre Welt digital sichtbar machen und Gäste schon vor dem Besuch emotional abholen wollen.",
          },
          {
            title: "Markenräume & Unternehmen",
            text: "Für Unternehmen, die ihre reale Umgebung als Teil der Marke inszenieren möchten.",
          },
        ]}
      />

      <ExperienceBlock
        overline="Anwendungen"
        title="Dein Place + PINE Places = Dein Markenraum."
        text="Ein Place ist mehr als ein Raum. Es ist der Ort, an dem Marke, Angebot, Atmosphäre und Entscheidung zusammenkommen. PINE Places macht daraus eine digitale Experience, die Kunden nicht nur informiert, sondern führt."
        items={[
          "Digitale Hotelführung",
          "Eventlocation-Präsentation",
          "Virtueller Showroom",
          "Digitale Customer Journey",
          "Markenraum für Sales und Beratung",
          "Digitale Vorschau für Gäste",
          "Interaktive Orientierung für komplexe Standorte",
          "Immersive Präsentation für Investoren, Kunden oder Partner",
        ]}
      />

      <ProductDetail
        forWhom={[
          "Hotels",
          "Resorts",
          "Immobilien",
          "Eventlocations",
          "Freizeitstandorte",
          "Showrooms",
          "Erlebniswelten",
          "Markenräume",
          "Touristische Betriebe",
        ]}
        strengths={[
          "Digitale Standortkopie",
          "Interaktive Customer Journey",
          "Markeninszenierung vor dem Besuch",
          "Bessere Orientierung",
          "Stärkere Wahrnehmung",
          "Emotionalere Entscheidungshilfe",
          "Digitale Präsenz mit echtem Standortbezug",
        ]}
        orientation={[
          "Übersetzt reale Orte in digitale Erfahrungsräume",
          "Online sichtbar, begehbar, erklärbar, emotional verständlich",
          "Aus einem Standort wird ein Markenraum",
        ]}
        ctaLabel="PINE Places kennenlernen"
      />

      <ProductCTA primaryLabel="PINE Places kennenlernen" />

      <RelatedSystems currentSlug="pine-places" />
      <SystemCombinations />

      <ProductFaq
        productSlug="pine-places"
        title="Häufige Fragen zu PINE Places."
        qa={PLACES_PRODUCT_FAQ}
      />

      <GeoDefinition
        term="PINE Places"
        overline="GEO · Definition"
        definition={PLACES_DEFINITION}
        qa={PLACES_QA_FOR_LD.slice(0, 1)}
        variant="muted"
      />
    </>
  );
}
